import { useEffect, useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useAlert } from "../../../../../context/AlertContext";
import {
  deleteTrackingReplay,
  type TrackingReplay,
} from "../../../../../store/admin/trackingReplaysStore";
import { useTrackingReplaysList } from "../../../../../features/trackingReplays/queries";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import type { ThemeClasses } from "./types";
import { TrackingReplaysHeader } from "./components/TrackingReplaysHeader";
import { TrackingReplaysList } from "./components/TrackingReplaysList";
import { TrackingReplaysPagination } from "./components/TrackingReplaysPagination";
import { TrackingReplayCreateModal, type RecordingConfig } from "./components/TrackingReplayCreateModal";
import { TrackingReplayViewerModal } from "./components/TrackingReplayViewerModal";

const scrollbarStyles = `
  .scrollbar-light::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-light::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  .scrollbar-dark::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-dark::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export default function TrackingReplaysSection() {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation("adminSections");
  const { t: tGeneral } = useTranslation("general");
  const { showAlert } = useAlert();

  // Theme classes
  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Estados
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = window.innerWidth >= 1536 ? 8 : 6;

  // Deleting state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Selected replay for modal
  const [selectedReplay, setSelectedReplay] = useState<TrackingReplay | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Company store - para verificar si hay compañía seleccionada
  const companyId = useCompaniesStore((state) => state.companyId);

  const filterParams = useMemo(
    () => ({ page: currentPage, limit: itemsPerPage }),
    [currentPage, itemsPerPage],
  );
  const replaysQuery = useTrackingReplaysList(filterParams);
  const trackingReplays = replaysQuery.data?.data ?? [];
  const loading = replaysQuery.isLoading;
  const meta = replaysQuery.data?.meta;

  const queryClient = useQueryClient();
  const invalidateReplays = () =>
    queryClient.invalidateQueries({ queryKey: ["trackingReplays"] });

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handlers de vista
  const handleViewChange = (view: "grid" | "list") => {
    setSelectedView(view);
  };

  const handleView = useCallback((replay: TrackingReplay) => {
    setSelectedReplay(replay);
    setShowViewModal(true);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedReplay(null);
    document.body.style.overflow = "";
  };

  // Handlers para crear
  const handleAddNew = useCallback(() => {
    setShowCreateModal(true);
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    document.body.style.overflow = "";
  }, []);

  const handleStartRecording = useCallback((config: RecordingConfig) => {
    // Crear config object para pasar a la nueva pestaña
    const fullConfig = {
      ...config,
      latitude: 0,
      longitude: 0,
      speed: null,
      engineStatus: null,
      battery: null,
      gps_sat: null,
      status: "unknown",
      heading: null,
    };

    // Guardar config en sessionStorage con clave única
    const configKey = `tracking-replay-config-${Date.now()}`;
    sessionStorage.setItem(configKey, JSON.stringify(fullConfig));

    // Abrir nueva pestaña con la página de grabación
    const url = `/tracking-replay/recording?configKey=${encodeURIComponent(configKey)}`;
    window.open(url, "_blank", "noopener,noreferrer");

    // Cerrar modal
    handleCloseCreateModal();
  }, [handleCloseCreateModal]);

  const handleDelete = useCallback(async (replay: TrackingReplay) => {
    if (!companyId) {
      await Swal.fire({
        title: t("trackingReplays.title"),
        text: t("common:selectCompanyTooltip", {
          defaultValue: "Selecciona una compañía para continuar",
        }),
        icon: "warning",
        confirmButtonColor: "#2563eb",
      });
      return;
    }

    const result = await Swal.fire({
      title: t("common:alerts.deleteConfirmTitle", { defaultValue: "¿Eliminar grabación?" }),
      text: t("common:alerts.deleteConfirmText", { name: replay.name, defaultValue: `¿Estás seguro de eliminar "${replay.name}"?` }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("common:alerts.deleteConfirmButton", { defaultValue: "Sí, eliminar" }),
      cancelButtonText: t("common:alerts.cancelButton", { defaultValue: "Cancelar" }),
    });

    if (!result.isConfirmed) return;

    setDeletingId(replay.id);

    await deleteTrackingReplay(
      replay.id,
      showAlert,
      () => {},
      tGeneral,
      () => {
        setDeletingId(null);
        invalidateReplays();
      }
    );
  }, [showAlert, tGeneral, invalidateReplays, companyId, t]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="h-full flex flex-col">
        {/* Header */}
        <TrackingReplaysHeader
          themeClasses={themeClasses}
          selectedView={selectedView}
          totalItems={meta?.totalItems || 0}
          canCreate={!!companyId}
          onViewChange={handleViewChange}
          onAddNew={handleAddNew}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <TrackingReplaysList
            trackingReplays={trackingReplays}
            loading={loading}
            selectedView={selectedView}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            deletingId={deletingId}
            onView={handleView}
            onDelete={handleDelete}
          />

          {/* Paginación */}
          <TrackingReplaysPagination
            currentPage={currentPage}
            totalPages={meta?.totalPages || 0}
            isDarkMode={isDarkMode}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Modal de Crear */}
        <TrackingReplayCreateModal
          isOpen={showCreateModal}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          onClose={handleCloseCreateModal}
          onStartRecording={handleStartRecording}
        />

        {/* Modal de Reproducción */}
        {showViewModal && selectedReplay && (
          <TrackingReplayViewerModal
            isOpen={showViewModal}
            replay={selectedReplay}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            onClose={handleCloseViewModal}
          />
        )}
      </div>
    </>
  );
}
